import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import CircularProgress from '@material-ui/core/CircularProgress';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';

import api from '../../../services';
import { canManageOrganization } from '../../../utils/userRoles';
import StatusBadge from '../../../components/ui/StatusBadge';

const useStyles = makeStyles((theme) => ({
  panel: {
    padding: theme.spacing(1, 0),
    maxWidth: 560,
  },
  card: {
    padding: theme.spacing(2),
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border)',
    marginBottom: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  brand: {
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    fontSize: 12,
    color: 'var(--text-secondary)',
  },
  stripeBox: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : 'var(--gray-50)',
  },
}));

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': { color: '#aab7c4' },
    },
    invalid: { color: '#9e2146' },
  },
};

const useInnerStyles = makeStyles((theme) => ({
  stripeBox: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : 'var(--gray-50)',
  },
}));

function AddCardInner({ clientSecret, onClose, onSuccess }) {
  const innerClasses = useInnerStyles();
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    const card = elements.getElement(CardElement);
    if (!card) return;
    setBusy(true);
    try {
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card },
      });
      if (error) {
        toast.error(error.message || 'Card setup failed');
        setBusy(false);
        return;
      }
      const pmId =
        typeof setupIntent.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent.payment_method && setupIntent.payment_method.id;
      if (!pmId) {
        toast.error('No payment method returned');
        setBusy(false);
        return;
      }
      await api.post('/tenant/billing/payment-methods/attach', { paymentMethodId: pmId });
      toast.success('Card saved');
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save card');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" paragraph>
          Your card details are sent securely to Stripe. We only store non-sensitive references on our servers.
        </Typography>
        <div className={innerClasses.stripeBox}>
          <CardElement options={cardElementOptions} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button color="primary" variant="contained" onClick={handleSubmit} disabled={!stripe || busy}>
          {busy ? <CircularProgress size={22} color="inherit" /> : 'Save card'}
        </Button>
      </DialogActions>
    </>
  );
}

export default function PaymentMethodTab() {
  const classes = useStyles();
  const canManage = canManageOrganization();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState('');

  const pk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
  const stripePromise = useMemo(() => (pk ? loadStripe(pk) : null), [pk]);

  const loadMethods = async () => {
    if (!canManage) return;
    try {
      const { data } = await api.get('/tenant/billing/payment-methods');
      setMethods(Array.isArray(data.paymentMethods) ? data.paymentMethods : []);
    } catch (e) {
      if (e.response?.status !== 403) {
        toast.error(e.response?.data?.message || 'Could not load payment methods');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAdd = async () => {
    if (!stripePromise) {
      toast.error('Stripe publishable key is not configured (REACT_APP_STRIPE_PUBLISHABLE_KEY).');
      return;
    }
    try {
      const { data } = await api.post('/tenant/billing/setup-intent');
      if (!data.clientSecret) {
        toast.error('Could not start card setup');
        return;
      }
      setClientSecret(data.clientSecret);
      setDialogOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Billing is not available');
    }
  };

  const removeMethod = async (id) => {
    if (!window.confirm('Remove this payment method?')) return;
    try {
      await api.delete(`/tenant/billing/payment-methods/${id}`);
      toast.success('Removed');
      await loadMethods();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Remove failed');
    }
  };

  const makeDefault = async (id) => {
    try {
      await api.patch(`/tenant/billing/payment-methods/${id}/default`);
      toast.success('Default updated');
      await loadMethods();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Update failed');
    }
  };

  if (!canManage) {
    return (
      <div className={classes.panel}>
        <Typography variant="body2" color="textSecondary">
          Only organization administrators can manage payment methods.
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.panel}>
      <Typography variant="h6" gutterBottom>
        Payment methods
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Add a debit or credit card for your organization. Cards are processed by Stripe.
      </Typography>
      <Box mb={2}>
        <Button variant="contained" color="primary" onClick={openAdd} disabled={!pk}>
          Add card
        </Button>
        {!pk ? (
          <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: 8 }}>
            Configure REACT_APP_STRIPE_PUBLISHABLE_KEY to enable card capture.
          </Typography>
        ) : null}
      </Box>
      {loading ? (
        <CircularProgress size={28} />
      ) : methods.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No saved cards yet.
        </Typography>
      ) : (
        methods.map((m) => (
          <Paper key={m._id} className={classes.card} elevation={0} variant="outlined">
            <div>
              <span className={classes.brand}>{m.brand || 'Card'}</span>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                •••• {m.last4 || '****'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {m.expMonth && m.expYear ? `Expires ${m.expMonth}/${m.expYear}` : ''}
              </Typography>
              {m.isDefault ? (
                <Box mt={0.5}>
                  <StatusBadge tone="success">Default</StatusBadge>
                </Box>
              ) : null}
            </div>
            <Box display="flex" style={{ gap: 8 }}>
              {!m.isDefault ? (
                <Button size="small" onClick={() => makeDefault(m._id)}>
                  Set default
                </Button>
              ) : null}
              <Button size="small" color="secondary" onClick={() => removeMethod(m._id)}>
                Remove
              </Button>
            </Box>
          </Paper>
        ))
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setClientSecret('');
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add card</DialogTitle>
        {clientSecret && stripePromise ? (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <AddCardInner
              clientSecret={clientSecret}
              onClose={() => {
                setDialogOpen(false);
                setClientSecret('');
              }}
              onSuccess={loadMethods}
            />
          </Elements>
        ) : (
          <DialogContent>
            <CircularProgress />
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
